import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle, ArrowRight, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/clientWeb/components/ui/button'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

interface SubscriptionResultProps {
  success: boolean
}

export default function SubscriptionResult({ success }: SubscriptionResultProps) {
  const t = useTranslation()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
        className="w-full max-w-md text-center"
      >
        {success ? (
          <>
            {/* Success */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              className="w-24 h-24 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-500" />
            </motion.div>

            {/* Confetti dots */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 0, x: 0 }}
                animate={{ opacity: [0, 1, 0], y: -60 - i * 10, x: (i % 2 === 0 ? 1 : -1) * (20 + i * 15) }}
                transition={{ delay: 0.2 + i * 0.05, duration: 1 }}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  background: ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#f97316', '#10b981'][i],
                  left: '50%', top: '40%',
                }}
              />
            ))}

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <h1 className="text-2xl font-bold text-foreground mb-2">{t("licenses.subscriptionActive")}</h1>
              <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                {t("licenses.subscriptionActiveDesc")}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="bg-card border border-border rounded-2xl p-4 mb-6 text-left space-y-2">
              {[
                t("licenses.unlimitedMeetings"),
                t("licenses.fullAnalytics"),
                t("licenses.recordingTranscription"),
                t("licenses.prioritySupport"),
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  <span className="text-foreground">{f}</span>
                </div>
              ))}
            </motion.div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 gap-2" onClick={() => navigate('/settings/licenses')}>
                <Home className="w-4 h-4" />{t("licenses.managePlan")}
              </Button>
              <Button className="flex-1 gap-2" onClick={() => navigate('/meetings')}>
                {t("licenses.getStarted")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Failed */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-6"
            >
              <XCircle className="w-12 h-12 text-red-500" />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <h1 className="text-2xl font-bold text-foreground mb-2">{t("licenses.paymentFailed")}</h1>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                {t("licenses.paymentFailedDesc")}
              </p>
            </motion.div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4 mb-6 text-left">
              <p className="text-xs font-medium text-red-700 dark:text-red-400">{t("licenses.commonReasons")}</p>
              <ul className="mt-2 space-y-1 text-xs text-red-600 dark:text-red-400">
                <li>• {t("licenses.cardDeclined")}</li>
                <li>• {t("licenses.insufficientBalance")}</li>
                <li>• {t("licenses.expiredCard")}</li>
                <li>• {t("licenses.networkIssue")}</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 gap-2" onClick={() => navigate('/settings/licenses')}>
                <Home className="w-4 h-4" />{t("common.cancel")}
              </Button>
              <Button className="flex-1 gap-2" onClick={() => navigate('/settings/licenses/cart')}>
                <RefreshCw className="w-4 h-4" />{t("common.tryAgain")}
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  )
}
